/**
 * SIL-440 — the SIL-426 carbon-disclosure copy inventory (§1-5, §10-11),
 * VERBATIM, as the single source of truth for every string the scenario
 * results carbon surfaces render.
 *
 * Source: `resources/plans/sil-426-carbon-disclosure-copy.md` (legally vetted
 * 2026-08-02 against ghg-scope3.pdf / ghg-scope2.pdf / the Scope 3
 * Calculation Guidance — 1 blocker + 7 wording fixes applied there).
 *
 * RULES (spec §7):
 *   - Never reword, abbreviate, or "tighten" a string here. A copy change is
 *     a legal-review change, not a UI change.
 *   - Never introduce the words "capital goods", "offset", "credit", or
 *     "carbon neutral" anywhere near these surfaces.
 *   - Conformance verbs are "consistent with", never "per".
 *   - Constants, not inline JSX literals, precisely so a legal re-vet can
 *     diff this one file against the inventory.
 */

/** §1 — global framing. Every carbon surface, chart footer, export header. */
export const CARBON_FRAMING_LINE = 'Planning forecast — not a GHG inventory.';

/** §2 — location-based method badge + market-based note. */
export const CARBON_METHOD_BADGE = 'Location-based method';
export const CARBON_METHOD_NOTE =
  'Operational emissions use location-based grid-average factors (source and version in the methodology sheet). Market-based Scope 2 (your contractual instruments, RECs, PPAs) is not modeled. A GHG inventory under the Scope 2 Guidance would report both methods.';

/** §3 — constant-grid-intensity caveat (chart footnote, SIL-423). */
export const CARBON_CONSTANT_GRID_CAVEAT =
  'Grid intensity is held constant over the horizon; actual grids are expected to decarbonize, so later-year operational figures are likely overstated.';

/** §5 — unlocated assets. */
export const CARBON_UNLOCATED_ASSETS_NOTE =
  'Assets without a location use the world-average grid factor and are assumed to run in operated (non-colocation) facilities.';

/** §11 — abatement ranking caption. */
export const CARBON_ABATEMENT_CAPTION =
  '$/tCO2e computed on official footprint deltas only. Advisory figures never enter this ranking.';

// ---------------------------------------------------------------------------
// SIL-443 (W5) — carbon sensitivity tornado.
//
// NOT in the SIL-426 inventory (the tornado was charted as "ship last", after
// the copy vet). Per spec §7, any new carbon-adjacent string gets a
// legal-consistency check against the inventory's conventions; these were
// written to them: no "capital goods"/"offset"/"credit"/"carbon neutral";
// no conformance claim at all (so no "per" vs "consistent with" question
// arises); the firewall sentence is a deliberate near-mirror of the §11
// abatement caption so the two quarantine statements read identically.
// ---------------------------------------------------------------------------

export const CARBON_SENSITIVITY_TITLE = 'Carbon sensitivity — swing on footprint delta';

/** The firewall statement, mirroring §11's abatement caption. */
export const CARBON_SENSITIVITY_CAPTION =
  'Swing computed on official footprint deltas only. Advisory figures never enter this chart.';

/** Method note — what "one-at-a-time" means and what the bounds are not. */
export const CARBON_SENSITIVITY_METHOD_NOTE =
  "Each driver is varied one at a time while every other driver is held at this scenario's own value. The tested ranges are planning bounds, not measured uncertainty, and they do not compound.";

/** Prefix for a driver this scenario does not carry. Never a zero-length bar:
 * a driver that cannot be tested must not read as one that was tested and
 * found not to matter. */
export const CARBON_SENSITIVITY_NOT_APPLICABLE_PREFIX = 'Not applicable';

/** Shown when the whole tornado has nothing to test. */
export const CARBON_SENSITIVITY_EMPTY =
  'No carbon drivers to test — this scenario has no cohorts that draw power, dispose hardware, or purchase used.';

/** §12 — chart label for the target line matching the active filter scope. */
export function carbonOrgTargetLabel(tco2ePerYr: number): string {
  return `Org target: ${tco2ePerYr.toLocaleString('en-US', { maximumFractionDigits: 1 })} tCO2e/yr`;
}

// ---------------------------------------------------------------------------
// §10 — advisory panel (baked into the panel, screenshot-safe)
// ---------------------------------------------------------------------------

export const CARBON_ADVISORY_HEADER =
  'Advisory — not your footprint. Never summed with the forecast.';

export const CARBON_ADVISORY_AVOIDED_VS_NEW_LABEL = 'Avoided vs new';
export const CARBON_ADVISORY_AVOIDED_VS_NEW_BASELINE =
  'Baseline: the same SKUs purchased new. Difference between new-SKU embodied carbon and the used-purchase adder.';

/** RENAMED per the legal vet — "deferred", never "avoided". */
export const CARBON_ADVISORY_DISPOSAL_DEFERRED_LABEL = 'Disposal deferred';
export const CARBON_ADVISORY_DISPOSAL_DEFERRED_BASELINE =
  'Disposal deferred (vs ITAD-now baseline). Baseline: the same cohort routed to ITAD disposal instead of sale. The unit will still reach end-of-life under a future owner.';

export const CARBON_ADVISORY_DOWNSTREAM_REUSE_LABEL = 'Downstream reuse';
export const CARBON_ADVISORY_DOWNSTREAM_REUSE_BASELINE =
  'Baseline: each sold unit replaced by a newly manufactured equivalent downstream.';

/**
 * Panel completeness footer — the non-exclusive note lives HERE (moved by the
 * legal vet) so it governs all three numbers and survives a cropped
 * screenshot. Never render an advisory number without this footer on the same
 * card.
 */
export const CARBON_ADVISORY_FOOTER =
  'Advisory estimates cover the same scenario period and organizational boundary as the forecast. They are computed against hypothetical baselines and may be incomplete. These are non-exclusive claims — other parties in the value chain may claim the same avoided emissions. Never combined with, netted against, or deducted from any footprint.';
